import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { auth, enquiries as enqApi } from '../lib/api';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  LogOut, Search, RefreshCw, Trash2, Mail, Phone, Calendar, Download, Filter,
  Inbox, CheckCircle, Clock, TrendingUp, MessageCircle, Eye, Reply, AlertCircle, Loader2, ExternalLink
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All Enquiries' },
  { value: 'pending', label: 'Pending' },
  { value: 'read', label: 'Read' },
  { value: 'replied', label: 'Replied' },
];

const STATUS_BADGES = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertCircle, label: 'Pending' },
  read: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Eye, label: 'Read' },
  replied: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Replied' },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const isAuthed = auth.isAuthenticated();

  const fetchData = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true);
    try {
      const [enqRes, statsRes] = await Promise.all([
        enqApi.list({ status: statusFilter, search }),
        enqApi.stats(),
      ]);
      setItems(enqRes.items || []);
      setStats(statsRes);
    } catch (err) {
      toast({ title: 'Failed to load', description: err?.response?.data?.detail || 'Try refreshing', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, toast, isAuthed]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!isAuthed) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = () => {
    auth.logout();
    navigate('/admin/login');
  };

  const updateStatus = async (id, status) => {
    try {
      await enqApi.updateStatus(id, status);
      toast({ title: 'Status updated', description: `Marked as ${status}` });
      fetchData();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    try {
      await enqApi.remove(id);
      toast({ title: 'Deleted', description: 'Enquiry removed' });
      if (selected?.id === id) setSelected(null);
      fetchData();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const exportCSV = () => {
    const rows = [['Name', 'Phone', 'Email', 'Subject', 'Message', 'Source', 'Status', 'Date']];
    items.forEach(it => {
      rows.push([
        it.name, it.phone, it.email || '', it.subject || '',
        (it.message || '').replace(/"/g, '""'), it.source, it.status,
        new Date(it.created_at).toLocaleString('en-IN'),
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = (enq) => {
    setSelected(enq);
    if (enq.status === 'pending') updateStatus(enq.id, 'read');
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f7941e] to-[#e67e00] flex items-center justify-center">
              <Inbox className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-display uppercase text-lg tracking-wide text-gray-900">Admin Dashboard</h1>
              <p className="text-[11px] text-gray-500">Ess Bee Heaters Pvt. Ltd.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/cms" className="hidden sm:inline-flex items-center gap-1.5 text-sm bg-[#fff7e6] text-[#e67e00] hover:bg-[#ffeacb] px-3 py-2 rounded-md font-semibold">
              <ExternalLink className="w-4 h-4" /> Content Manager
            </Link>
            <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#f7941e] px-3 py-2">
              <ExternalLink className="w-4 h-4" /> View site
            </Link>
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <StatCard icon={Inbox} label="Total" value={stats?.total ?? '-'} color="from-gray-700 to-gray-900" />
          <StatCard icon={Clock} label="Pending" value={stats?.pending ?? '-'} color="from-yellow-500 to-yellow-700" />
          <StatCard icon={Eye} label="Read" value={stats?.read ?? '-'} color="from-blue-500 to-blue-700" />
          <StatCard icon={CheckCircle} label="Replied" value={stats?.replied ?? '-'} color="from-green-500 to-green-700" />
          <StatCard icon={TrendingUp} label="Today" value={stats?.today ?? '-'} color="from-[#f7941e] to-[#e67e00]" />
          <StatCard icon={Calendar} label="This Week" value={stats?.this_week ?? '-'} color="from-purple-500 to-purple-700" />
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by name, phone, email, message..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-200 rounded-md text-sm bg-white"
              >
                {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <button onClick={fetchData} title="Refresh" className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={exportCSV} title="Export CSV" className="inline-flex items-center gap-1.5 text-sm bg-[#f7941e] hover:bg-[#e67e00] text-white px-3 py-2 rounded-md">
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
        </div>

        {/* Enquiries List */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-2">
            {loading && items.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#f7941e]" />
                <p className="text-sm text-gray-500 mt-2">Loading enquiries...</p>
              </div>
            ) : items.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Inbox className="w-12 h-12 mx-auto text-gray-300" />
                <p className="text-gray-600 font-display uppercase mt-3">No enquiries found</p>
                <p className="text-sm text-gray-500 mt-1">{search || statusFilter ? 'Try changing the filters' : 'Submissions will appear here'}</p>
              </div>
            ) : (
              items.map(enq => <EnquiryCard key={enq.id} enq={enq} onOpen={openDetail} active={selected?.id === enq.id} />)
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:sticky lg:top-20 self-start">
            {selected ? (
              <DetailPanel enq={selected} onUpdate={updateStatus} onDelete={removeEnquiry} onClose={() => setSelected(null)} />
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <MessageCircle className="w-10 h-10 mx-auto text-gray-300" />
                <p className="text-sm text-gray-500 mt-2">Select an enquiry to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
      <div className={`w-9 h-9 rounded-md bg-gradient-to-br ${color} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <p className="text-2xl font-display font-bold text-gray-900 leading-tight">{value}</p>
      <p className="text-[11px] text-gray-500 uppercase tracking-wider">{label}</p>
    </div>
  );
}

function EnquiryCard({ enq, onOpen, active }) {
  const badge = STATUS_BADGES[enq.status] || STATUS_BADGES.pending;
  const BadgeIcon = badge.icon;
  return (
    <button
      onClick={() => onOpen(enq)}
      className={`w-full text-left bg-white rounded-lg border p-4 hover:shadow-md transition-all ${active ? 'border-[#f7941e] ring-2 ring-[#f7941e]/30' : 'border-gray-200'}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-gray-900 truncate">{enq.name}</h3>
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <Phone className="w-3 h-3" /> {enq.phone}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${badge.bg} ${badge.text}`}>
          <BadgeIcon className="w-3 h-3" /> {badge.label}
        </span>
      </div>
      {enq.message && <p className="text-sm text-gray-600 line-clamp-2 mb-2">{enq.message}</p>}
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(enq.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
        <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded">{enq.source}</span>
      </div>
    </button>
  );
}

function DetailPanel({ enq, onUpdate, onDelete, onClose }) {
  const badge = STATUS_BADGES[enq.status];
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-[#fff7e6] to-[#ffeacb] p-4 border-b border-[#f4e6c6] flex items-center justify-between">
        <h3 className="font-display uppercase tracking-wide text-sm text-gray-900">Enquiry Details</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl leading-none">&times;</button>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <p className="text-[11px] uppercase text-gray-500 tracking-wider">Name</p>
          <p className="font-display font-semibold text-gray-900">{enq.name}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] uppercase text-gray-500 tracking-wider">Phone</p>
            <a href={`tel:${enq.phone}`} className="text-[#f7941e] hover:underline text-sm flex items-center gap-1">
              <Phone className="w-3 h-3" />{enq.phone}
            </a>
          </div>
          {enq.email && (
            <div>
              <p className="text-[11px] uppercase text-gray-500 tracking-wider">Email</p>
              <a href={`mailto:${enq.email}`} className="text-[#f7941e] hover:underline text-sm break-all flex items-center gap-1">
                <Mail className="w-3 h-3" />{enq.email}
              </a>
            </div>
          )}
        </div>
        {enq.subject && (
          <div>
            <p className="text-[11px] uppercase text-gray-500 tracking-wider">Subject</p>
            <p className="text-sm text-gray-900">{enq.subject}</p>
          </div>
        )}
        {enq.message && (
          <div>
            <p className="text-[11px] uppercase text-gray-500 tracking-wider">Message</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded border border-gray-100">{enq.message}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] uppercase text-gray-500 tracking-wider">Source</p>
            <p className="text-sm text-gray-700">{enq.source}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase text-gray-500 tracking-wider">Status</p>
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${badge.bg} ${badge.text}`}>
              <badge.icon className="w-3 h-3" /> {badge.label}
            </span>
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase text-gray-500 tracking-wider">Received</p>
          <p className="text-sm text-gray-700">{new Date(enq.created_at).toLocaleString('en-IN')}</p>
        </div>

        <div className="pt-3 border-t border-gray-100 space-y-2">
          <p className="text-[11px] uppercase text-gray-500 tracking-wider">Quick Actions</p>
          <div className="grid grid-cols-2 gap-2">
            <a href={`tel:${enq.phone}`} className="inline-flex items-center justify-center gap-1.5 text-sm bg-green-50 text-green-700 hover:bg-green-100 px-3 py-2 rounded-md">
              <Phone className="w-4 h-4" /> Call
            </a>
            <a href={`https://wa.me/${enq.phone.replace(/\D/g, '')}?text=${encodeURIComponent('Hello ' + enq.name + ', regarding your enquiry with Ess Bee Heaters...')}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 text-sm bg-green-100 text-green-800 hover:bg-green-200 px-3 py-2 rounded-md">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            {enq.email && (
              <a href={`mailto:${enq.email}?subject=Re: Your enquiry to Ess Bee Heaters`} className="inline-flex items-center justify-center gap-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-2 rounded-md col-span-2">
                <Reply className="w-4 h-4" /> Email Reply
              </a>
            )}
          </div>
        </div>

        <div className="pt-3 border-t border-gray-100">
          <p className="text-[11px] uppercase text-gray-500 tracking-wider mb-2">Update Status</p>
          <div className="grid grid-cols-3 gap-2">
            {['pending', 'read', 'replied'].map(s => (
              <button
                key={s}
                onClick={() => onUpdate(enq.id, s)}
                disabled={enq.status === s}
                className={`text-xs uppercase tracking-wider py-2 rounded-md transition-colors ${enq.status === s ? 'bg-[#f7941e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <button onClick={() => onDelete(enq.id)} className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-md mt-3">
          <Trash2 className="w-4 h-4" /> Delete Enquiry
        </button>
      </div>
    </div>
  );
}
